import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.#attachMobileProductNavigation(signal);

    queueMicrotask(() => {
      this.#attachDesktopViewer(signal);
    });
  }

  #controller = new AbortController();
  #mobileNavigation = null;
  #mobileCurrentIndex = 0;
  #mobileHideTimer = undefined;
  #mobileScrollFrame = undefined;
  #mobileResizeObserver = undefined;
  #mobilePointerActive = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#teardownMobileProductNavigation();
    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #lastWheelNavigationAt = 0;

  #attachDesktopViewer(signal) {
    if (!this.closest('.product-information')) return;

    const mainArea = this.querySelector('[data-pdp-media-main]');
    const mainImage = this.querySelector('[data-pdp-media-image]');
    const thumbnails = this.querySelectorAll('[data-pdp-media-thumb]');
    if (!(mainArea instanceof HTMLElement) || !(mainImage instanceof HTMLImageElement) || !thumbnails.length) return;

    thumbnails.forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLButtonElement)) return;

      thumbnail.addEventListener(
        'click',
        () => {
          const index = Number.parseInt(thumbnail.dataset.index ?? '', 10);
          if (Number.isNaN(index)) return;

          this.#setDesktopMedia(index);
        },
        { signal }
      );
    });

    mainArea.addEventListener(
      'wheel',
      (event) => {
        this.#handleDesktopWheelNavigation(event);
      },
      {
        passive: false,
        signal,
      }
    );

    this.#syncDesktopThumbnails(0);
  }

  #handleDesktopWheelNavigation(event) {
    if (!mediaQueryLarge.matches) return;

    const thumbnails = Array.from(this.querySelectorAll('[data-pdp-media-thumb]'));
    if (thumbnails.length <= 1) return;

    if (Math.abs(event.deltaY) < 10) return;

    const now = Date.now();
    if (now - this.#lastWheelNavigationAt < 260) {
      event.preventDefault();
      return;
    }

    this.#lastWheelNavigationAt = now;
    event.preventDefault();

    const currentIndex = thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-selected') === 'true');
    const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex =
      event.deltaY > 0
        ? Math.min(thumbnails.length - 1, safeCurrentIndex + 1)
        : Math.max(0, safeCurrentIndex - 1);

    this.#setDesktopMedia(nextIndex);
  }

  #handleSlideshowSelect = (event) => {
    if (mediaQueryLarge.matches) {
      this.#setDesktopMedia(event.detail.index);
      return;
    }

    this.#setMobileProductNavigationIndex(event.detail.index);
  };

  #attachMobileProductNavigation(signal) {
    if (!this.closest("main[data-template='product'] .product-information")) return;

    const nav = this.querySelector('[data-product-media-mobile-nav]');
    const progressTrack = this.querySelector('[data-product-media-progress-track]');
    const indicator = this.querySelector('[data-product-media-progress-indicator]');
    const counter = this.querySelector('[data-product-media-counter]');
    const previousButton = this.querySelector('[data-product-media-prev]');
    const nextButton = this.querySelector('[data-product-media-next]');
    const track = this.#getMobileProductMediaTrack();

    if (
      !(nav instanceof HTMLElement) ||
      !(progressTrack instanceof HTMLElement) ||
      !(indicator instanceof HTMLElement) ||
      !(counter instanceof HTMLElement) ||
      !(previousButton instanceof HTMLButtonElement) ||
      !(nextButton instanceof HTMLButtonElement) ||
      !(track instanceof HTMLElement)
    ) {
      return;
    }

    this.#mobileNavigation = {
      nav,
      progressTrack,
      indicator,
      counter,
      previousButton,
      nextButton,
      track,
    };

    track.addEventListener('scroll', this.#handleMobileProductMediaScroll, { passive: true, signal });
    track.addEventListener('pointerdown', this.#handleMobileInteractionStart, { passive: true, signal });
    track.addEventListener('touchstart', this.#handleMobileInteractionStart, { passive: true, signal });
    window.addEventListener('pointerup', this.#handleMobileInteractionEnd, { passive: true, signal });
    window.addEventListener('pointercancel', this.#handleMobileInteractionEnd, { passive: true, signal });
    window.addEventListener('touchend', this.#handleMobileInteractionEnd, { passive: true, signal });
    window.addEventListener('touchcancel', this.#handleMobileInteractionEnd, { passive: true, signal });
    previousButton.addEventListener('click', this.#handleMobilePreviousClick, { signal });
    nextButton.addEventListener('click', this.#handleMobileNextClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMobileMediaQueryChange, { signal });
    window.addEventListener('resize', this.#updateMobileProductNavigation, { signal });
    window.addEventListener('orientationchange', this.#updateMobileProductNavigation, { signal });

    if ('ResizeObserver' in window) {
      this.#mobileResizeObserver = new ResizeObserver(this.#updateMobileProductNavigation);
      this.#mobileResizeObserver.observe(progressTrack);
      this.#mobileResizeObserver.observe(track);
    }

    this.#setMobileProductNavigationIndex(this.#getCurrentMobileProductMediaIndex());
    this.#syncMobileProductNavigationMode();
    queueMicrotask(this.#updateMobileProductNavigation);
  }

  #teardownMobileProductNavigation() {
    window.clearTimeout(this.#mobileHideTimer);
    window.cancelAnimationFrame(this.#mobileScrollFrame);
    this.#mobileResizeObserver?.disconnect();
    this.#mobileNavigation = null;
    this.#mobilePointerActive = false;
  }

  #getMobileProductMediaTrack() {
    const slideshowTrack = this.querySelector(':scope > slideshow-component > slideshow-container > slideshow-slides');
    if (slideshowTrack instanceof HTMLElement) return slideshowTrack;

    const gridTrack = this.querySelector(':scope > .media-gallery__grid');
    if (gridTrack instanceof HTMLElement) return gridTrack;

    return null;
  }

  #getMobileProductMediaSlides() {
    const track = this.#mobileNavigation?.track ?? this.#getMobileProductMediaTrack();
    if (!(track instanceof HTMLElement)) return [];

    return Array.from(track.children).filter(
      (slide) =>
        slide instanceof HTMLElement &&
        !slide.hasAttribute('hidden') &&
        (slide.matches('slideshow-slide.product-media-container') || slide.matches('.product-media-container'))
    );
  }

  #syncMobileProductNavigationMode() {
    const navigation = this.#mobileNavigation;
    if (!navigation) return;

    const shouldHide = mediaQueryLarge.matches || this.#getMobileProductMediaSlides().length <= 1;

    navigation.nav.hidden = shouldHide;
    navigation.previousButton.hidden = shouldHide;
    navigation.nextButton.hidden = shouldHide;

    if (shouldHide) {
      this.#hideMobileInteractionUi();
      return;
    }

    this.#updateMobileProductNavigation();
  }

  #setMobileProductNavigationIndex(index) {
    const slides = this.#getMobileProductMediaSlides();
    if (!slides.length) return;

    this.#mobileCurrentIndex = Math.max(0, Math.min(index, slides.length - 1));
    this.#updateMobileProductNavigation();
  }

  #updateMobileProductNavigation = () => {
    const navigation = this.#mobileNavigation;
    if (!navigation) return;

    const slides = this.#getMobileProductMediaSlides();
    const total = slides.length;
    const shouldHide = mediaQueryLarge.matches || total <= 1;

    navigation.nav.hidden = shouldHide;
    navigation.previousButton.hidden = shouldHide;
    navigation.nextButton.hidden = shouldHide;

    if (shouldHide) return;

    const currentIndex = Math.max(0, Math.min(this.#mobileCurrentIndex, total - 1));
    const trackWidth = navigation.progressTrack.getBoundingClientRect().width;

    if (trackWidth > 0) {
      const minIndicatorWidth = 12;
      const indicatorWidth = Math.min(trackWidth, Math.max(minIndicatorWidth, trackWidth / total));
      const maxTravel = Math.max(0, trackWidth - indicatorWidth);
      const progressRatio = total > 1 ? currentIndex / (total - 1) : 0;
      const x = maxTravel * progressRatio;

      navigation.indicator.style.setProperty('--media-progress-width', `${indicatorWidth}px`);
      navigation.indicator.style.setProperty('--media-progress-x', `${x}px`);
    }

    navigation.counter.textContent = `${currentIndex + 1} / ${total}`;
    navigation.previousButton.disabled = currentIndex <= 0;
    navigation.nextButton.disabled = currentIndex >= total - 1;
  };

  #handleMobileProductMediaScroll = () => {
    if (!this.#isMobileProductNavigationActive() || this.#mobileScrollFrame) return;

    this.#mobileScrollFrame = window.requestAnimationFrame(() => {
      this.#mobileScrollFrame = undefined;
      this.#setMobileProductNavigationIndex(this.#getCurrentMobileProductMediaIndex());

      if (this.classList.contains('is-interacting') && !this.#mobilePointerActive) {
        this.#scheduleMobileInteractionHide();
      }
    });
  };

  #handleMobileInteractionStart = () => {
    if (!this.#isMobileProductNavigationActive() || this.#getMobileProductMediaSlides().length <= 1) return;

    this.#mobilePointerActive = true;
    this.#showMobileInteractionUi();
  };

  #handleMobileInteractionEnd = () => {
    if (!this.#mobilePointerActive && !this.classList.contains('is-interacting')) return;

    this.#mobilePointerActive = false;
    this.#scheduleMobileInteractionHide();
  };

  #handleMobilePreviousClick = (event) => {
    this.#handleMobileArrowClick(-1, event);
  };

  #handleMobileNextClick = (event) => {
    this.#handleMobileArrowClick(1, event);
  };

  #handleMobileArrowClick(direction, event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.#isMobileProductNavigationActive()) return;

    const slides = this.#getMobileProductMediaSlides();
    if (slides.length <= 1) return;

    const targetIndex = Math.max(0, Math.min(this.#mobileCurrentIndex + direction, slides.length - 1));

    this.#showMobileInteractionUi();
    this.#goToMobileProductMedia(targetIndex, event);
    this.#scheduleMobileInteractionHide();
  }

  #handleMobileMediaQueryChange = () => {
    this.#mobilePointerActive = false;
    this.#syncMobileProductNavigationMode();
  };

  #isMobileProductNavigationActive() {
    return Boolean(this.#mobileNavigation && !mediaQueryLarge.matches);
  }

  #showMobileInteractionUi() {
    window.clearTimeout(this.#mobileHideTimer);
    this.classList.add('is-interacting');
  }

  #scheduleMobileInteractionHide() {
    window.clearTimeout(this.#mobileHideTimer);
    this.#mobileHideTimer = window.setTimeout(() => {
      this.#hideMobileInteractionUi();
    }, 1000);
  }

  #hideMobileInteractionUi() {
    window.clearTimeout(this.#mobileHideTimer);
    this.classList.remove('is-interacting');
  }

  #goToMobileProductMedia(index, event) {
    const navigation = this.#mobileNavigation;
    const slides = this.#getMobileProductMediaSlides();
    if (!navigation || !slides.length) return;

    const boundedIndex = Math.max(0, Math.min(index, slides.length - 1));
    const target = slides[boundedIndex];
    if (!(target instanceof HTMLElement)) return;

    if (this.slideshow && typeof this.slideshow.select === 'function') {
      this.slideshow.select(boundedIndex, event, { animate: !prefersReducedMotion() });
    } else {
      navigation.track.scrollTo({
        left: this.#getSlideScrollLeft(navigation.track, target),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    this.#setMobileProductNavigationIndex(boundedIndex);
  }

  #getCurrentMobileProductMediaIndex() {
    const navigation = this.#mobileNavigation;
    const slides = this.#getMobileProductMediaSlides();
    if (!navigation || !slides.length) return 0;

    const scrollLeft = navigation.track.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const distance = Math.abs(this.#getSlideScrollLeft(navigation.track, slide) - scrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  #getSlideScrollLeft(track, slide) {
    const trackRect = track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();

    return slideRect.left - trackRect.left + track.scrollLeft;
  }

  #setDesktopMedia(index) {
    const mainImage = this.querySelector('[data-pdp-media-image]');
    const mainSource = this.querySelector('[data-pdp-media-source]');
    const thumbnails = Array.from(this.querySelectorAll('[data-pdp-media-thumb]'));
    if (!(mainImage instanceof HTMLImageElement) || !thumbnails.length) return;

    const thumbnail = thumbnails[index];
    if (!(thumbnail instanceof HTMLButtonElement)) return;

    const nextSrc = thumbnail.dataset.fullSrc;
    const nextSrcset = thumbnail.dataset.fullSrcset;
    const nextAlt = thumbnail.dataset.alt ?? '';
    const focalStyle = thumbnail.dataset.focalStyle ?? '';

    if (mainSource instanceof HTMLSourceElement && nextSrcset) {
      mainSource.srcset = nextSrcset;
    } else if (nextSrc) {
      mainImage.src = nextSrc;
    }
    mainImage.alt = nextAlt;
    mainImage.style.cssText = focalStyle;

    this.#syncDesktopThumbnails(index);
  }

  #syncDesktopThumbnails(index) {
    const thumbnails = this.querySelectorAll('[data-pdp-media-thumb]');

    thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.setAttribute('aria-selected', `${thumbnailIndex === index}`);
    });
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
